import { useSelector } from 'store';
import useAuth from './useAuth';
import useErrorSnackbar from './useErrorSnackbar';
import notifyCaptureSuppressed from 'utils/notifyCaptureSuppressed';

/** nodemailer attachment shape, passed through to the `mail` doc. Prefer `path`
 *  (a download URL) over inline base64 `content` so the `mail` doc stays under
 *  Firestore's 1 MiB document limit for large PDFs. */
export interface EmailAttachment {
  filename: string;
  path?: string;
  content?: string;
  encoding?: string;
  contentType?: string;
}

export interface FirebaseEmail {
  to: string;
  cc?: string | string[];
  message: {
    subject: string;
    html: string;
    attachments?: EmailAttachment[];
  };
}

export type SendEmailResult = boolean | 'captured';

export default function useSendEmail() {
  const { db, user } = useAuth();
  const mailRef = db.collection('mail');
  const errorSnackbar = useErrorSnackbar();
  const { emailActive } = useSelector((state) => state.config?.mainConfig?.app || {});
  const captureNotifications = useSelector(
    (state) => (state.config?.mainConfig as any)?.notificationSettings?.captureNotifications === true
  );

  /**
   * `true` = a mail doc was queued; `false` = nothing sent. `'captured'` = the
   * capture flag swallowed it: truthy, so callers that only need "did the send
   * path succeed" keep working, while a caller that RECORDS a delivery (Deliver's
   * emailed stamps) checks `=== true`.
   */
  const sendEmail = async (emailObject: FirebaseEmail): Promise<SendEmailResult> => {
    // --- CAPTURE ---
    // config/notificationSettings.captureNotifications on → log, don't deliver.
    if (captureNotifications) {
      console.log('[CAPTURE] email suppressed — not delivered', {
        to: emailObject.to,
        subject: emailObject.message?.subject,
        hasAttachments: !!emailObject.message?.attachments?.length
      });
      notifyCaptureSuppressed('Email');
      return 'captured';
    }
    if (!emailActive && user?.isStaff) {
      errorSnackbar({ message: 'Email is not active' } as Error);
      return false;
    }

    try {
      if (!emailObject.to) throw new Error('No email address provided');
      const docRef = await mailRef.add({ ...emailObject });
      console.log('Email sent with ID: ', docRef.id);
      return true;
    } catch (error) {
      console.error('Error adding email: ', error);
      return false;
    }
  };

  return sendEmail;
}
